/**
 * ArchLang v0.10 — advisory findings query (S13b, engine layer).
 *
 * `advisoryFindings(policies, ctx)` runs the policy engine and returns ONLY the
 * `severity === "advisory"` findings. Advisory findings are **report artifacts,
 * not diagnostics** (design §4/§5): `policyDiagnostics` deliberately drops them
 * (`diagnosticSeverityOf("advisory") === undefined`). This query is the data the
 * advisory report panel lists later (S15) — the report-side counterpart of the
 * diagnostic-side `policyDiagnostics`.
 *
 * It reuses `evalPolicies` verbatim (waivers + cross-policy escalations applied),
 * keeps only the ACTIVE (non-waived) advisory-severity findings — the same
 * `activeFindings` gate the diagnostic surface uses — and returns them in a
 * deterministic order: by anchor source (file, line, column), then by message.
 */

import type { PolicyDecl } from "./v010-ast.js";
import type { EvalContext } from "./selector-eval.js";
import {
  evalPolicies,
  activeFindings,
  type Finding,
  type EvalPoliciesOptions,
} from "./policy-eval.js";

/**
 * The advisory (report-only) findings of a policy set. Runs {@link evalPolicies}
 * (escalations may LIFT a finding TO advisory, or a base advisory UP past it —
 * the join is applied first), keeps only active `advisory` findings, and sorts
 * them deterministically (source, then message). `opts.onError` skips policies
 * that fail to evaluate, mirroring the diagnostic surface.
 */
export function advisoryFindings(
  policies: readonly PolicyDecl[],
  ctx: EvalContext,
  opts?: EvalPoliciesOptions,
): Finding[] {
  const active = activeFindings(evalPolicies(policies, ctx, opts));
  return active.filter((f) => f.severity === "advisory").sort(compareFindings);
}

/** Deterministic finding order: by anchor source (file/line/col), then message. */
function compareFindings(a: Finding, b: Finding): number {
  const s = compareSource(a, b);
  return s !== 0 ? s : messageOf(a).localeCompare(messageOf(b));
}

function compareSource(a: Finding, b: Finding): number {
  const sa = a.anchorSource ?? a.source;
  const sb = b.anchorSource ?? b.source;
  if (!sa || !sb) return sa ? -1 : sb ? 1 : 0; // spanless findings sort last
  const f = (sa.file ?? "").localeCompare(sb.file ?? "");
  if (f !== 0) return f;
  if (sa.start.line !== sb.start.line) return sa.start.line - sb.start.line;
  return sa.start.column - sb.start.column;
}

/** The finding's report message — mirrors the diagnostic-stream wording. */
function messageOf(f: Finding): string {
  return f.ruleKind === "forbid"
    ? `${f.anchorKey} is forbidden by policy ${f.policy}`
    : `${f.anchorKey} violates policy ${f.policy} (required relationship missing)`;
}
